import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NewTemplatePage from '../../NewTemplatePage';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import useBrands from '../../hooks/useBrands';
import useMarkets from '../../hooks/useMarkets';
import { addTemplate } from '../../hooks/useTemplates';
import { normalizeQrMarginsForSave, validateTemplateForSave } from '../../wysiwyg/templateNormalization';
import {
  getErrorBody,
  getErrorMessage,
  throwIfLegacyResultError,
} from '../../utils/requestErrors';

export default function NewTemplateContainer() {
  const navigate = useNavigate();
  const { org, currentUser } = useLegacyAppSession();
  const {
    data: organizationMarkets,
    error: organizationMarketsError,
    isLoading: areOrganizationMarketsLoading,
  } = useMarkets({
    enabled: Boolean(org?.id),
    organizationId: org?.id,
    page: 0,
    pageSize: 200,
    scope: 'organization',
  });
  const { data: availableMarkets, error: availableMarketsError } = useMarkets({
    enabled: Boolean(org?.id),
    organizationId: org?.id,
    scope: 'all',
  });
  const { data: organizationBrands, error: organizationBrandsError } = useBrands({
    enabled: Boolean(org?.id),
    organizationId: org?.id,
    loadAll: true,
  });

  useEffect(() => {
    if (organizationMarketsError) {
      showToastError('Template markets could not be loaded.');
    }
  }, [organizationMarketsError]);

  useEffect(() => {
    if (availableMarketsError) {
      showToastError('Market configuration could not be loaded. TAC calculation is disabled.');
    }
  }, [availableMarketsError]);

  useEffect(() => {
    if (organizationBrandsError) {
      showToastError('Template brands could not be loaded.');
    }
  }, [organizationBrandsError]);

  if (org?.id && areOrganizationMarketsLoading) {
    return null;
  }

  const marketIds = (organizationMarkets || []).map((market) => market.id);
  const availableBrands = (organizationBrands || [])
    .map((brand) => ({ id: Number(brand.id), name: brand.name }))
    .filter((brand) => Number.isFinite(brand.id))
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));
  const brandIds = availableBrands.map((brand) => brand.id);

  return (
    <NewTemplatePage
      key={org?.id}
      onClose={() => navigate('/templates', { replace: true })}
      onSave={async (template, nextMarketIds) => {
        if (!org?.id) return;

        const validationErrors = validateTemplateForSave(template);
        if (validationErrors.length > 0) {
          showToastError(`Template could not be created: ${validationErrors[0]}`);
          console.error('Template validation failed:', validationErrors);
          return;
        }

        try {
          const savedTemplate = normalizeQrMarginsForSave(template);
          const result = await addTemplate({
            organizationId: org.id,
            name: template.name,
            content: JSON.stringify(savedTemplate),
            marketIds: nextMarketIds,
          });
          throwIfLegacyResultError(result, 'Please try again.');
          showToastSuccess('Template successfully added.');
          navigate('/templates', { replace: true });
        } catch (error) {
          const serverErrors = (getErrorBody(error) as { errors?: string[] })?.errors;
          showToastError(
            serverErrors?.length
              ? `Template could not be created: ${serverErrors[0]}`
              : `Template could not be created: ${getErrorMessage(error, 'Please try again.')}`,
          );
          console.error('Failed to create template:', error);
        }
      }}
      organizationId={org?.id ? String(org.id) : undefined}
      userId={currentUser?.id}
      marketIds={marketIds}
      availableMarkets={availableMarkets}
      brandIds={brandIds}
      availableBrands={availableBrands}
    />
  );
}
